import React from 'react';
import { authService, syncAPIBaseURL } from '../services/api';
import { setEditionPreference, getEditionFromBackend, normalizeEdition, setAPIBaseURL, Edition } from '../config/editions';
import { supabaseAuthService, isSupabaseConfigured } from '../services/supabase';
import { CLIENT_DEMO_TOKEN, DEMO_STORAGE_KEY, TRAIL_ACTIVE_KEY, TRAIL_STEP_KEY, isClientDemoToken } from '../demo/constants';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role?: string;
  tenantId?: string;
  mfaEnabled?: boolean;
  edition: Edition;
  demo?: boolean;
}

export interface RegisterResult {
  requiresConfirmation?: boolean;
  email?: string;
  mfaRequired?: boolean;
  mfaSession?: string;
}

export type LoginResult = RegisterResult;

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  edition: Edition;
  isAuthenticated: boolean;
  isDemo: boolean;
  loading: boolean;
  supabaseEnabled: boolean;
  backendReachable: boolean;
  login: (email: string, password: string, edition: Edition) => Promise<LoginResult>;
  register: (
    name: string,
    email: string,
    password: string,
    edition: Edition,
    extras?: { first_name?: string; last_name?: string; organisation?: string; job_title?: string; country?: string; industry?: string; organisation_size?: string; accept_terms?: boolean },
  ) => Promise<RegisterResult>;
  supabaseLogin: (email: string, password: string, edition: Edition) => Promise<void>;
  supabaseRegister: (name: string, email: string, password: string, edition: Edition) => Promise<RegisterResult>;
  demoLogin: (edition: Edition) => Promise<void>;
  verifyMfa: (email: string, code: string, session: string, edition: Edition) => Promise<void>;
  refreshMe: () => Promise<void>;
  updateProfile: (name: string) => Promise<void>;
  changePassword: (currentPassword: string, nextPassword: string) => Promise<void>;
  logout: () => void;
  setEdition: (edition: Edition) => void;
  persistAuth: (payload: any) => void;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

function readStoredAuth() {
  try {
    const token = localStorage.getItem('auth_token');
    const userRaw = localStorage.getItem('auth_user');
    const edition = normalizeEdition(localStorage.getItem('app_edition')) || 'community';
    const user = userRaw ? JSON.parse(userRaw) : null;
    const demo = Boolean(user?.demo) || localStorage.getItem(DEMO_STORAGE_KEY) === '1' || isClientDemoToken(token);
    if (user) user.demo = demo;
    return { token, user, edition };
  } catch {
    return { token: null, user: null, edition: 'community' as Edition };
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = React.useState(true);
  const [token, setToken] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [edition, setEditionState] = React.useState<Edition>('community');
  const [backendReachable, setBackendReachable] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const stored = readStoredAuth();

      let sbSession: Awaited<ReturnType<typeof supabaseAuthService.getSession>> | null = null;
      try {
        sbSession = await supabaseAuthService.getSession();
      } catch {
        sbSession = null;
      }
      let sbUser: AuthUser | null = null;
      let sbToken: string | null = null;
      if (sbSession?.user?.email) {
        sbUser = {
          id: sbSession.user.id,
          name: (sbSession.user.user_metadata as any)?.name || sbSession.user.email.split('@')[0],
          email: sbSession.user.email,
          edition: normalizeEdition((sbSession.user.user_metadata as any)?.edition),
        };
        sbToken = sbSession.access_token ?? null;
      }

      let remote: { edition: string; features: Record<string, any>; baseURL: string } | null = null;
      try {
        remote = await getEditionFromBackend();
      } catch {
        remote = null;
      }
      if (cancelled) return;

      // Backend /edition reports the *server build* (the OSS server says
      // "oss"), not the user's entitlement. Prefer the persisted edition so a
      // previously selected Enterprise/Professional workspace survives reloads
      // instead of snapping back to Community and hiding features.
      const detectedEdition = normalizeEdition(sbUser?.edition || stored.edition || remote?.edition);
      if (remote?.edition) {
        syncAPIBaseURL();
        if (!sbUser?.edition && !stored.edition) {
          setEditionPreference(detectedEdition);
        }
      }

      setBackendReachable(Boolean(remote?.edition));
      // Static Pages: drop a stale absolute API host so demo scans do not
      // fall through to a dead network target from a prior local session.
      if (!remote?.edition) {
        try {
          const storedApi = localStorage.getItem('api_base_url');
          if (storedApi && /^https?:\/\//i.test(storedApi)) {
            localStorage.removeItem('api_base_url');
            setAPIBaseURL('/api/v1');
          }
        } catch {
          /* ignore */
        }
      }
      const nextToken = sbToken ?? stored.token;
      let nextUser: AuthUser | null = sbUser ?? stored.user;
      let nextEdition = detectedEdition;

      if (nextToken && !isClientDemoToken(nextToken) && remote?.edition) {
        try {
          const me = await authService.me();
          const data = me.data || {};
          const syncedEdition = normalizeEdition(data.edition || nextEdition);
          nextUser = {
            id: String(data.id || data.user_id || nextUser?.id || ''),
            name: String(data.name || nextUser?.name || ''),
            email: String(data.email || nextUser?.email || ''),
            role: String(data.role || nextUser?.role || 'viewer'),
            tenantId: String(data.tenant_id || nextUser?.tenantId || ''),
            mfaEnabled: Boolean(data.mfa_enabled),
            edition: syncedEdition,
            demo: false,
          };
          nextEdition = syncedEdition;
          try {
            localStorage.setItem('auth_user', JSON.stringify(nextUser));
            setEditionPreference(syncedEdition);
          } catch {
            /* ignore */
          }
        } catch {
          // Keep stored session if /auth/me is unreachable.
        }
      }
      if (cancelled) return;

      setToken(nextToken);
      setUser(nextUser);
      setEditionState(nextEdition);
      setLoading(false);
    };

    init().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const persist = React.useCallback((nextToken: string | null, nextUser: AuthUser | null, nextEdition: Edition) => {
    try {
      if (nextToken) {
        localStorage.setItem('auth_token', nextToken);
      } else {
        localStorage.removeItem('auth_token');
      }
      if (nextUser) {
        localStorage.setItem('auth_user', JSON.stringify(nextUser));
      } else {
        localStorage.removeItem('auth_user');
      }
      setEditionPreference(nextEdition);
    } catch {
      // ignore storage failures
    }
    setToken(nextToken);
    setUser(nextUser);
    setEditionState(nextEdition);
  }, []);

  const completeAuth = React.useCallback((payload: any) => {
    const token = payload?.access_token || payload?.token;
    if (!token || !payload?.user) {
      throw new Error('Authentication service returned an invalid response');
    }
    const nextToken = String(token);
    const nextEdition = normalizeEdition(payload?.edition || payload.user.edition || 'community');
    const nextUser = {
      ...payload.user,
      id: payload.user.id || payload.user.user_id,
      tenantId: payload.user.tenant_id || payload.user.tenantId,
      mfaEnabled: Boolean(payload.user.mfa_enabled ?? payload.user.mfaEnabled),
      edition: nextEdition,
      demo: Boolean(payload?.demo_mode || payload?.user?.demo),
    };
    try {
      if (nextUser.demo) localStorage.setItem(DEMO_STORAGE_KEY, '1');
      else localStorage.removeItem(DEMO_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    persist(nextToken, nextUser, nextEdition);
  }, [persist]);

  const supabaseLogin = React.useCallback(async (email: string, password: string, nextEdition: Edition) => {
    const supabaseUser = await supabaseAuthService.signIn(email, password);
    const sbToken = await supabaseAuthService.getSessionToken();
    persist(sbToken, supabaseUser, normalizeEdition(supabaseUser.edition || nextEdition));
  }, [persist]);

  const supabaseRegister = React.useCallback(async (name: string, email: string, password: string, nextEdition: Edition): Promise<RegisterResult> => {
    const { user: supabaseUser, session } = await supabaseAuthService.signUp(name, email, password, nextEdition);
    if (session?.access_token) {
      persist(session.access_token, supabaseUser, nextEdition);
      return { requiresConfirmation: false };
    }
    return { requiresConfirmation: true, email: supabaseUser.email };
  }, [persist]);

  const login = React.useCallback(async (email: string, password: string, nextEdition: Edition): Promise<LoginResult> => {
    if (backendReachable) {
      const response = await authService.login({ email, password, edition: nextEdition });
      const data = response.data;
      if (data?.mfa_required) {
        return { mfaRequired: true, mfaSession: data.mfa_session, email };
      }
      completeAuth(data);
      return { requiresConfirmation: false };
    }
    if (isSupabaseConfigured) {
      const supabaseUser = await supabaseAuthService.signIn(email, password);
      const sbToken = await supabaseAuthService.getSessionToken();
      persist(sbToken, supabaseUser, normalizeEdition(supabaseUser.edition || nextEdition));
      return { requiresConfirmation: false };
    }
    throw new Error(
      'No engine is connected on this Pages build, so password sign-in cannot run here. Use Interactive Demo (labelled sample data) or request an Enterprise workspace via sales@rivicq.com.'
    );
  }, [backendReachable, completeAuth, persist]);

  const register = React.useCallback(async (
    name: string,
    email: string,
    password: string,
    nextEdition: Edition,
    extras?: { first_name?: string; last_name?: string; organisation?: string; job_title?: string; country?: string; industry?: string; organisation_size?: string; accept_terms?: boolean },
  ): Promise<RegisterResult> => {
    if (backendReachable) {
      const response = await authService.register({ name, email, password, edition: nextEdition, ...extras });
      const data = response.data;
      if (data?.mfa_required) {
        return { requiresConfirmation: true, email, mfaRequired: true, mfaSession: data.mfa_session };
      }
      completeAuth(data);
      return { requiresConfirmation: false };
    }
    if (isSupabaseConfigured) {
      return supabaseRegister(name, email, password, nextEdition);
    }
    throw new Error(
      'No engine is connected on this Pages build, so registration cannot run here. Use Interactive Demo (labelled sample data) or request an Enterprise workspace via sales@rivicq.com.'
    );
  }, [backendReachable, completeAuth, supabaseRegister]);

  const verifyMfa = React.useCallback(async (email: string, code: string, session: string, nextEdition: Edition) => {
    const response = await authService.verifyMfa({ email, mfa_code: code, mfa_session: session, edition: nextEdition });
    completeAuth(response.data);
  }, [completeAuth]);

  const refreshMe = React.useCallback(async () => {
    if (!token || isClientDemoToken(token) || !backendReachable) return;
    const me = await authService.me();
    const data = me.data || {};
    const nextEdition = normalizeEdition(data.edition || edition);
    persist(token, {
      id: String(data.id || data.user_id || user?.id || ''),
      name: String(data.name || user?.name || ''),
      email: String(data.email || user?.email || ''),
      role: String(data.role || user?.role || 'viewer'),
      tenantId: String(data.tenant_id || user?.tenantId || ''),
      mfaEnabled: Boolean(data.mfa_enabled),
      edition: nextEdition,
      demo: Boolean(user?.demo),
    }, nextEdition);
  }, [backendReachable, edition, persist, token, user]);

  const updateProfile = React.useCallback(async (name: string) => {
    await authService.patchMe({ name });
    await refreshMe();
  }, [refreshMe]);

  const changePassword = React.useCallback(async (currentPassword: string, nextPassword: string) => {
    await authService.changePassword(currentPassword, nextPassword);
  }, []);

  const demoLogin = React.useCallback(async (_nextEdition: Edition) => {
    if (backendReachable) {
      const response = await authService.demo('community');
      completeAuth({ ...response.data, demo_mode: true, edition: 'oss' });
      return;
    }
    // Isolated Pages/static demo — not a JWT, not mixed with customer data.
    const demoUser: AuthUser = {
      id: 'demo-user',
      name: 'Demo operator',
      email: 'demo-ciso@demo.rivicq.local',
      role: 'operator',
      tenantId: 'tenant-demo',
      mfaEnabled: false,
      edition: 'community',
      demo: true,
    };
    try {
      localStorage.setItem(DEMO_STORAGE_KEY, '1');
      localStorage.removeItem('api_base_url');
    } catch {
      /* ignore */
    }
    setAPIBaseURL('/api/v1');
    syncAPIBaseURL();
    persist(CLIENT_DEMO_TOKEN, demoUser, 'community');
  }, [backendReachable, completeAuth, persist]);

  const logout = React.useCallback(() => {
    if (backendReachable && token && !isClientDemoToken(token)) {
      authService.logout().catch(() => undefined);
    }
    if (isSupabaseConfigured) {
      supabaseAuthService.signOut().catch(() => undefined);
    }
    try {
      localStorage.removeItem(DEMO_STORAGE_KEY);
      sessionStorage.removeItem(TRAIL_ACTIVE_KEY);
      sessionStorage.removeItem(TRAIL_STEP_KEY);
    } catch {
      /* ignore */
    }
    persist(null, null, edition);
  }, [backendReachable, edition, persist, token]);

  const setEdition = React.useCallback((nextEdition: Edition) => {
    if (user?.demo) {
      persist(token, user, 'community');
      return;
    }
    persist(token, user, nextEdition);
  }, [persist, token, user]);

  const isDemo = Boolean(user?.demo) || isClientDemoToken(token);

  const value = React.useMemo(() => ({
    user,
    token,
    edition,
    isAuthenticated: Boolean(token),
    isDemo,
    loading,
    supabaseEnabled: isSupabaseConfigured,
    backendReachable,
    login,
    register,
    supabaseLogin,
    supabaseRegister,
    demoLogin,
    verifyMfa,
    refreshMe,
    updateProfile,
    changePassword,
    logout,
    setEdition,
    persistAuth: completeAuth,
  }), [edition, isDemo, loading, backendReachable, login, register, supabaseLogin, supabaseRegister, demoLogin, verifyMfa, refreshMe, updateProfile, changePassword, logout, setEdition, token, user, completeAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
